import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostBinding,
  OnInit,
  signal,
  WritableSignal,
  computed,
  viewChild,
  effect,
  HostListener,
  Signal,
} from '@angular/core';
import { Assistants, Chats, Resources, Search } from '@local/client-contracts';
import { Constants } from '@local/common';
import { getTimestampFromRelativeTime, RelativeTimeModel } from '@local/ts-infra';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { LogService } from '@shared/services';
import { FlagsService } from '@shared/services/flags.service';
import { Logger } from '@unleash-tech/js-logger';
import { cloneDeep, isEqual } from 'lodash';
import { NgScrollbar } from 'ngx-scrollbar';
import { distinctUntilChanged, filter, firstValueFrom, from, map, Subscription, take, tap } from 'rxjs';
import { FiltersService } from 'src/app/bar/services/filters.service';
import { HubService } from 'src/app/bar/services/hub.service';
import { SearchOptions, SearchService, SearchSession } from 'src/app/bar/services/search';
import { AnswersSourceSettings } from 'src/app/bar/services/search/client';
import { AnswerCompletionTypes, AnswerSearchItem } from '../../../results';
import { ChatsPopupService } from '../../services/chats-popup.service';
import { AssistantChatData, NEW_CHAT_ID } from '../../model';
import { ChatResourcesService } from '../../services/chat-resources.service';
import { ChatsService } from '../../services/chats.service';
import { RouterService } from '@shared/services/router.service';
import { CHAT_PAGE_PATH, CHAT_PAGE_PATH_SESSION, SIDE_PANEL_PAGE_PATH, SIDE_PANEL_CHAT_PAGE_PATH } from 'src/app/bar/utils/constants';
import { ChatTextareaComponent } from '../chat-textarea/chat-textarea.component';
import { ChatFileService } from '../../services/chat-files.service';
import { AssistantsService } from '@shared/services/assistants.service';
import { UploadBlobService } from 'src/app/bar/services/blobs/upload-blob.service';
import { ExperiencesService } from 'src/app/bar/services/experiences.service';
import { generateId, isExtension } from '@local/common-web';
import { toSignal } from '@angular/core/rxjs-interop';
import { WritingAssistantService } from 'src/app/bar/services/writing-assistant.service';
import { ClientStorageService } from '@shared/services/client-storage.service';
import { isProdEnv } from '@shared/utils';

interface Feedback {
  feedbackType: Resources.FeedbackType;
  intent: Assistants.QuestionIntent;
  resources: Search.ResultResourceItem[];
  linkIds: string;
  resourceIds: string;
  index: number;
  searchId?: string;
}
type CurrentMessageStateType = 'loadingAnswer' | 'loadingNextPart' | 'typingCompleted' | 'interactiveTyping' | 'staticTyping';

@UntilDestroy()
@Component({
  selector: 'chat',
  templateUrl: './chat.component.html',
  styleUrls: ['./chat.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ChatComponent implements OnInit {
  private readonly RELATIVE_MODIFIED_SINCE: RelativeTimeModel = { quantity: 7, type: 'days' };
  private readonly MESSAGES_PER_PAGE = 20;
  private logger: Logger;
  private searchSession: SearchSession;
  private searchSubscription: Subscription;
  private previousScrollTop = 0;
  private query: string;
  private pageToken: string = undefined;
  private loadingNextPage: boolean = false;
  private subscribeHistory: Subscription;
  private isIgnoreChat: boolean;
  private initSubscription: Subscription;
  private latestChatId: Signal<string>;
  private currentQuestionId: string;
  private totalCountsFiles: WritableSignal<number> = signal(0);

  readonly INIT_FAILED_TEXT = 'Something went wrong. Please try again soon.';
  readonly ASSISTANT_DELETED_TEXT = 'The Assistant used for this thread is no longer available. ';
  readonly ASSISTANT_DELETED_TEXT_EXTENSION = 'Select a different Assistant.';

  feedback: Feedback;
  chatId: string = NEW_CHAT_ID;
  draftQuestion: WritableSignal<Chats.UserMessage> = signal(null);
  sessionId: string;
  currentMessageState: CurrentMessageStateType;
  assistantData: WritableSignal<AssistantChatData> = signal(null);
  enableDisclaimer = false;
  chatHistoryData: WritableSignal<Chats.ChatHistoryItem[]> = signal(null);
  chatHistoryDataLoading: WritableSignal<boolean> = signal(false);
  progressId: WritableSignal<string> = signal('');
  initStatus: WritableSignal<'loading' | 'error' | 'success'> = signal(null);
  showSearchId = signal(false);

  isExtension = isExtension();

  scrollRef = viewChild(NgScrollbar);
  answerFeedbackRef = viewChild<ElementRef>('answerFeedback');
  chatTextareaRef = viewChild(ChatTextareaComponent);

  isReadOnlyChat = computed(() => this.assistantData() && ['disabled', 'deleted'].includes(this.assistantData().state));

  allowFileUpload = computed(() => this.assistantData()?.knowledgeType === 'External' && this.assistantData()?.allowFileUpload);

  initCompleted = computed(() => !this.initStatus() || this.initStatus() === 'success');

  isEmptyState = computed(() => !this.chatHistoryDataLoading() && !this.chatHistoryData()?.length && !this.draftQuestion());

  showResumeLastThread = computed(() => !!this.latestChatId() && this.isExtension);

  get isLoadingAnswer() {
    return this.currentMessageState === 'loadingAnswer';
  }

  get isLoadingNextAnswerPart() {
    return this.currentMessageState === 'loadingNextPart';
  }

  get isInitFailed() {
    return this.initStatus() === 'error';
  }

  get messageInprogress() {
    return ['loadingAnswer', 'loadingNextPart', 'interactiveTyping', 'staticTyping'].includes(this.currentMessageState);
  }

  private get isNewChat() {
    return this.chatId === NEW_CHAT_ID;
  }

  private get queryParams() {
    return this.chatFileService.getChatFileQueryParams(this.chatId, this.assistantData()?.id);
  }

  private get isExternalChat() {
    return this.assistantData()?.knowledgeType === 'External';
  }

  @HostBinding('attr.isExtensionMode')
  get isExtensionMode() {
    return this.isExtension;
  }

  @HostListener('window:beforeunload', ['$event'])
  unloadHandler() {
    this.onLeftChat();
  }

  constructor(
    logService: LogService,
    private hubService: HubService,
    private chatsService: ChatsService,
    private chatsPopupService: ChatsPopupService,
    private searchService: SearchService,
    private cdr: ChangeDetectorRef,
    private filtersService: FiltersService,
    private chatResourcesService: ChatResourcesService,
    private flagsService: FlagsService,
    private routerService: RouterService,
    private chatFileService: ChatFileService,
    private uploadBlobService: UploadBlobService,
    private assistantService: AssistantsService,
    private experiencesService: ExperiencesService,
    private writingAssistantService: WritingAssistantService,
    private clientStorage: ClientStorageService
  ) {
    this.logger = logService.scope('ChatComponent');
    this.latestChatId = toSignal(this.chatsService.latestChatIdByCurrentChat$);
    effect(
      () => {
        this.previousScrollTop = 0;
        if (this.scrollRef()) {
          this.scrollToBottom();
          this.scrollRef()
            .scrolled.pipe(untilDestroyed(this))
            .subscribe((event) => {
              this.onScroll(event);
            });
        }
      },
      { allowSignalWrites: true }
    );
  }

  ngOnInit() {
    this.initChatMessages();
    this.initFlags();
  }

  ngOnDestroy() {
    this.onLeftChat();
    this.destroySearchSession();
    this.unsubscribeInit();
    this.chatResourcesService.resetCache();
  }

  private async initFlags() {
    this.flagsService.all$.pipe(untilDestroyed(this)).subscribe(async () => {
      this.enableDisclaimer = await this.flagsService.isEnabled(Constants.ENABLE_ANSWER_DISCLAIMER);
      this.cdr.markForCheck();
    });
    const hideSearchId = ((await this.clientStorage.get('developerFlags')) as any)?.hideSearchId?.value;
    this.showSearchId.set(!isProdEnv() && !hideSearchId);
  }

  private initChatMessages() {
    this.searchSession = this.searchService.getOrCreateSearchSession('Chat-page');

    this.hubService.readOnly = true;
    this.chatsService.currentChat$
      .pipe(
        filter((c) => !!c),
        untilDestroyed(this)
      )
      .subscribe((newChat) => {
        const newChatData = cloneDeep(newChat);
        this.assistantData.set(newChatData.assistant);
        setTimeout(() => {
          this.chatTextareaRef()?.focusTextarea();
        }, 0);
        if (newChatData.draftQuery) {
          return this.startChatSessionWithQuery(newChatData.draftQuery);
        }
      });
    this.chatsService.currentSessionId$.pipe(untilDestroyed(this)).subscribe(async (id) => {
      if (id === this.chatId) {
        return;
      }
      if (this.subscribeHistory) {
        this.subscribeHistory.unsubscribe();
      }
      this.chatHistoryDataLoading.set(false);
      this.onLeftChat();
      this.initParams();
      this.chatId = id;
      if (this.chatId && !this.isNewChat) {
        this.chatHistoryDataLoading.set(true);
        this.chatHistoryData.set([]);
        this.init();
        this.initSubscribeHistory();
        this.updateSession();
      } else {
        this.chatHistoryData.set([]);
      }
    });

    this.chatsService.forceStopChat$.pipe(untilDestroyed(this)).subscribe(() => {
      this.forceStopChat();
    });
    if (this.isExtension) {
      //Handle messages when navigation happens (message sent via state)
      this.chatsService.userMessage$.pipe(untilDestroyed(this)).subscribe((userMessage) => {
        if (userMessage) {
          this.sendHandler(userMessage);
        }
      });
      //Handle messages in current chat
      this.writingAssistantService.userMessage$.pipe(untilDestroyed(this)).subscribe((userMessage) => {
        if (this.messageInprogress) {
          this.cancelQuery();
        }
        this.sendHandler(userMessage);
      });
    }
  }

  private initSubscribeHistory() {
    this.subscribeHistory = this.chatsService
      .getChatHistory$(this.chatId, this.pageToken)
      .pipe(
        filter((res) => !!res),
        take(1)
      )
      .subscribe((data) => {
        this.chatsService.updateAnswersResources(data.items);
        this.chatFileService.updateRemoteFiles(data.items);
        this.chatHistoryData.update((chatActiveData) => [...data.items, ...chatActiveData]);
        this.chatHistoryDataLoading.set(false);
        this.pageToken = data.pageToken;
        this.chatTextareaRef()?.focusTextarea();
      });
  }

  private async updateSession() {
    const currentSession = (await firstValueFrom(this.chatsService.sessions$))?.find(
      (session) => session.id === this.chatId && !session.ignore
    );
    const filesCount = currentSession?.filesCount;
    if (filesCount) {
      this.totalCountsFiles.set(filesCount);
    }
  }

  init() {
    this.unsubscribeInit();
    if (!this.isExternalChat) {
      return;
    }
    this.initStatus.set('loading');
    this.initSubscription = from(this.assistantService.init({ chatId: this.chatId, experienceId: this.assistantData()?.id }))
      .pipe(take(1), untilDestroyed(this))
      .subscribe({
        next: () => {
          this.initStatus.set('success');
        },
        error: (error) => {
          this.logger.error('init failed in loading history', error);
          this.clearChatTextArea();
          this.initStatus.set('error');
        },
      });
  }

  private getNextPageMessages() {
    this.loadingNextPage = true;
    this.subscribeHistory = this.chatsService
      .getChatHistory$(this.chatId, this.pageToken)
      .pipe(
        filter((res) => !!res),
        take(1)
      )
      .subscribe((data) => {
        this.chatHistoryData.update((chatActiveData) => [...data.items, ...chatActiveData]);
        this.pageToken = data.pageToken;
        this.loadingNextPage = false;
      });
  }

  onQueryChange(query: string) {
    this.query = query;
  }

  resumeLastThread() {
    this.updateChatUrl(this.latestChatId(), false);
  }

  async sendHandler(draftQuestion?: Chats.UserMessage) {
    const currentQuestionId = generateId();
    this.currentQuestionId = currentQuestionId;

    if (draftQuestion) {
      this.draftQuestion.set(draftQuestion);
    } else {
      const filters = await this.filtersService.transformFiltersForDisplay(this.filtersService.allFilters);
      this.draftQuestion.set({ content: this.query, filters, timestamp: Date.now() });
      const files = this.chatFileService.getFiles(this.chatId);
      if (files?.length) {
        this.draftQuestion.update((draft) => {
          draft.blobIds = files.filter((f) => f.status === 'completed').map((a) => a.blobId);
          return draft;
        });
      }
      this.chatFileService.updateFilesInCache(files);
    }

    this.closeFeedback();
    this.chatTextareaRef()?.clearTextarea();
    this.chatFileService.resetFiles(this.chatId, this.draftQuestion().blobIds, this.queryParams);
    this.currentMessageState = 'loadingAnswer';
    this.cdr.markForCheck();
    setTimeout(() => {
      this.scrollToBottom();
    }, 0);

    if (this.isNewChat) {
      this.createNewSession();
      await this.chatsService.getChatSessionPromise(this.chatId);
    }

    //for the state where the response was stopped before a search was sent
    if (!this.isLoadingAnswer || this.currentQuestionId !== currentQuestionId) {
      return;
    }

    this.searchForAnswer();
  }

  private async searchForAnswer() {
    this.unsubscribeSearchSession();
    const options: SearchOptions = this.getSearchOptions();
    let first = true;
    const generateTitle = this.isIgnoreChat;

    this.searchSubscription = this.searchSession
      .search$(options)
      .pipe(
        untilDestroyed(this),
        distinctUntilChanged((prev, next) => isEqual(prev, next)),
        filter((ctx) => !!ctx.sources?.length),
        tap((ctx) => (this.sessionId = ctx.sessionId)),
        map((ctx) => ctx.sources?.find((item) => item.source.type === 'answers')?.items?.[0] as AnswerSearchItem),
        tap((result) => {
          if (result?.progress) {
            this.progressId.set(result.progress);

            if (result.state === 'Loading' && result.hasAnswer) {
              this.currentMessageState = 'loadingNextPart';
              this.chatHistoryData.update((chatHistoryData) => {
                const updatedChat = [...chatHistoryData];
                updatedChat[updatedChat.length - 1].assistantMessage = null;
                return updatedChat;
              });
            }
          }
        }),
        filter((result) => !!result && result.state !== 'Loading')
      )
      .subscribe((result: AnswerSearchItem) => {
        //notice: do not add async function in this section!!
        if (this.isIgnoreChat) {
          this.initialFirstMessage();
        }
        const newChatHistoryItem = this.createChatHistoryItem(result);
        if (newChatHistoryItem) {
          this.addLocalChatHistory(newChatHistoryItem, !first);
          this.handleCurrentState(result.state);
          if (AnswerCompletionTypes.includes(result.state)) {
            this.saveHistoryMessage(newChatHistoryItem, generateTitle);
          }
        }
        first = false;
        this.progressId.set(null);
      });
  }

  private getSearchOptions(): SearchOptions {
    const modifiedSince = this.isExternalChat ? null : getTimestampFromRelativeTime(this.RELATIVE_MODIFIED_SINCE);
    return {
      resetSession: false,
      query: this.draftQuestion().content, //new question
      sources: [
        {
          type: 'answers',
          source: 'Chat',
          minQueryLength: 1,
          minWordLength: 1,
          minWords: 1,
          maxCount: 1,
          chat: { id: this.chatId, modifiedSince, useHistory: !!this.chatHistoryData()?.length },
          allowAllQuestionQueries: true,
          assistantId: this.assistantData()?.id,
          preventFormattedAnswer: true,
          blobIds: this.draftQuestion().blobIds,
          action: this.draftQuestion().action,
          shouldClassifyAi: false,
        } as AnswersSourceSettings,
      ],
      trigger: 'user_query',
    };
  }

  onUploadFiles(files: File[]) {
    const validFiles = this.chatFileService.onUploadFiles(files, this.chatId, this.totalCountsFiles());
    if (!validFiles?.length) {
      return;
    }
    if (this.isNewChat) {
      this.createNewSession();
    }
    validFiles.map((file) =>
      this.uploadBlobService.onUploadFile(file, this.chatId, this.queryParams, this.chatsService.getChatSessionPromise(this.chatId))
    );
    this.totalCountsFiles.update((count) => count + validFiles.length);
  }

  onRemoveFile(blobId: string) {
    this.chatFileService.deleteFile(this.chatId, blobId, this.queryParams);
    this.totalCountsFiles.update((count) => count - 1);
  }

  private createNewSession() {
    this.isIgnoreChat = true;
    this.chatId = this.chatsService.createNewSession(this.assistantData()?.id, this.assistantData()?.knowledgeType, true);
  }

  private updateChatUrl(chatId: string, isNewSession = true) {
    const baseParams = [CHAT_PAGE_PATH, this.assistantData()?.id, CHAT_PAGE_PATH_SESSION, chatId].filter((item) => item);
    const routerParams = this.isExtension ? [SIDE_PANEL_PAGE_PATH, ...baseParams] : baseParams;
    this.routerService.navigate(routerParams, {
      state: { isNewSession },
      queryParamsHandling: 'merge',
    });
  }

  openNewChat() {
    this.forceStopChat();
    const assistantId = this.assistantData()?.id;
    const chatUrl = assistantId ? `/${SIDE_PANEL_CHAT_PAGE_PATH}/${assistantId}` : `/${SIDE_PANEL_CHAT_PAGE_PATH}`;
    this.routerService.navigateByUrl(chatUrl, { replaceUrl: true });
  }

  private initialFirstMessage() {
    this.isIgnoreChat = false;
    this.chatsService.updateSession({ chatId: this.chatId, cancelIgnore: true });
    this.updateChatUrl(this.chatId);
  }

  private unsubscribeSearchSession() {
    if (this.searchSubscription) {
      this.searchSubscription.unsubscribe();
      this.searchSubscription = undefined;
    }
  }

  private unsubscribeInit() {
    if (this.initSubscription) {
      this.initSubscription.unsubscribe();
      this.initSubscription = null;
    }
  }

  onAnswerTypedFinished() {
    this.currentMessageState = 'typingCompleted';
    this.scrollToBottom();
  }

  scrollToBottom(preventDuration?: boolean) {
    if (preventDuration) {
      this.scrollRef()?.scrollTo({ bottom: 0 });
      return;
    }
    this.scrollRef()?.scrollTo({ bottom: 0, duration: 0 });
  }

  private createChatHistoryItem(result?: AnswerSearchItem): Chats.ChatHistoryItem {
    switch (result.state) {
      case 'Full':
      case 'Cache':
      case 'Generating':
      case 'GeneratingDone':
        return this.createResultsAvailableAnswer(result);
      case 'NoResults':
      case 'RephraseRequired':
        return this.createNoResultsFoundAnswer(result);
    }
  }

  private createBaseChatHistoryItem(result: AnswerSearchItem): Chats.ChatHistoryItem {
    return {
      userMessage: {
        content: result.query,
        filters: this.draftQuestion()?.filters,
        timestamp: this.draftQuestion()?.timestamp,
        blobIds: this.draftQuestion()?.blobIds,
        action: this.draftQuestion()?.action,
      },
      chatId: this.chatId,
    };
  }

  private createResultsAvailableAnswer(result: AnswerSearchItem): Chats.ChatHistoryItem {
    const baseItem = this.createBaseChatHistoryItem(result);
    const resources = this.chatsService.convertToAnswerResources(result.resources);
    this.chatResourcesService.updateResourceInCache(result.resources);

    const assistantMessage: Chats.AssistantMessage = {
      content: result?.text,
      state: Chats.AssistantMessageState.ResultsAvailable,
      resources,
      searchId: result.searchId,
      timestamp: Date.now(),
      references: result.references,
      resourceIds: result.resourceIds,
      dataSources: result.dataSources,
      tools: result.tools,
      downloadFiles: result.downloadFiles,
    };
    if (result?.intent) {
      assistantMessage.intent = result.intent;
    }
    return {
      ...baseItem,
      assistantMessage: assistantMessage,
      modelId: this.assistantData()?.model,
    };
  }

  private createNoResultsFoundAnswer(result: AnswerSearchItem): Chats.ChatHistoryItem {
    const baseItem = this.createBaseChatHistoryItem(result);
    const state =
      result.state === 'RephraseRequired' ? Chats.AssistantMessageState.RephraseRequired : Chats.AssistantMessageState.NoResultsFound;
    const assistantMessage: Chats.AssistantMessage = {
      state,
      timestamp: Date.now(),
      searchId: result.searchId,
    };

    if (result?.intent) {
      assistantMessage.intent = result.intent;
    }

    if (result.reason) {
      this.assistantData.set({ ...this.assistantData(), state: result.reason === 'no-access' ? 'disabled' : result.reason });
      this.logger.error('Assistant becomes disabled', result.reason);
    }

    return {
      ...baseItem,
      assistantMessage: assistantMessage,
      modelId: this.assistantData()?.model,
    };
  }

  private addLocalChatHistory(newChatHistoryItem: Chats.ChatHistoryItem, generateInProgress?: boolean) {
    const chatHistory = this.chatHistoryData() ?? [];
    const lastIndex = chatHistory.length - 1;
    const lastChat = chatHistory[lastIndex];

    if (generateInProgress && lastChat) {
      this.chatHistoryData.update((chatHistoryData) => {
        chatHistoryData[lastIndex].assistantMessage = newChatHistoryItem.assistantMessage;
        return chatHistoryData;
      });
    } else {
      chatHistory.push(newChatHistoryItem);
      this.chatHistoryData.set(chatHistory);
    }
    setTimeout(() => {
      this.scrollToBottom();
    }, 0);
  }

  private handleCurrentState(answerState?: Assistants.AnswerStatusType) {
    if (answerState === 'Generating') {
      this.currentMessageState = 'staticTyping';
      this.cdr.markForCheck();
      return;
    }
    if (answerState === 'GeneratingDone') {
      this.currentMessageState = 'typingCompleted';
    } else {
      this.currentMessageState = 'interactiveTyping';
    }
    this.resetQuestionData();
  }

  private saveHistoryMessage(newChatHistoryItem: Chats.ChatHistoryItem, isGenerateName?: boolean) {
    delete newChatHistoryItem.assistantMessage.resourceIds;
    this.chatsService.createHistoryMessage(newChatHistoryItem, isGenerateName, this.totalCountsFiles());
  }

  showFeedback($event, index: number) {
    this.closeFeedback();
    const { resources, type, searchId } = $event;
    this.feedback = {
      feedbackType: type,
      intent: this.chatHistoryData()[index].assistantMessage.intent,
      resources: resources,
      linkIds: resources?.map((r) => r.link.id).join(','),
      resourceIds: resources?.map((r) => r.id).join(','),
      index,
      searchId,
    };
    this.cdr.markForCheck();

    setTimeout(() => {
      const isLastElement = this.chatHistoryData().length - 1 === index;
      if (isLastElement) {
        this.scrollToBottom();
      } else {
        this.scrollRef().scrollToElement(this.answerFeedbackRef()?.nativeElement, { duration: 500 });
      }
    }, 0);
  }

  closeFeedback() {
    if (this.feedback) {
      this.feedback = null;
      this.cdr.markForCheck();
    }
  }

  startChatSessionWithQuery(query: string) {
    this.query = query;
    this.sendHandler();
  }

  trackById = (index, item) => item?.id;

  onChangedAssistant(assistant: AssistantChatData) {
    this.forceStopChat();
    const chatUrl = assistant?.id ? `/${SIDE_PANEL_CHAT_PAGE_PATH}/${assistant.id}` : `/${SIDE_PANEL_CHAT_PAGE_PATH}`;
    this.routerService.navigateByUrl(chatUrl);
  }

  //in case that url changes
  private onLeftChat() {
    if (this.messageInprogress) {
      this.cancelQuery(true);
    } else if (this.isIgnoreChat) {
      this.chatsService.deleteSession(this.chatId);
    }
    this.clearChatTextArea();
    this.totalCountsFiles.set(0);
  }

  //in case that url not changes - only in the first message
  private forceStopChat() {
    this.clearChatTextArea();
    if (!this.isIgnoreChat) {
      return;
    }
    if (this.messageInprogress) {
      this.cancelQuery(true);
    } else if (!this.isNewChat) {
      //only in files
      this.chatsService.deleteSession(this.chatId);
      this.totalCountsFiles.set(0);
    }
    this.resetQuestionData();
    this.initParams();
  }

  cancelQuery(leftChat?: boolean) {
    this.progressId.set(null);
    if (!this.draftQuestion() || !this.currentMessageState) {
      return;
    }
    const currentMessageState = this.currentMessageState;
    this.currentMessageState = this.currentQuestionId = null;
    this.destroySearchSession();
    const newChatHistoryItem = this.chatsService.handleLeftChat(
      this.chatId,
      this.assistantData(),
      currentMessageState,
      this.chatHistoryData(),
      this.draftQuestion(),
      this.isIgnoreChat,
      this.totalCountsFiles()
    );
    if (leftChat) {
      return;
    }
    if (newChatHistoryItem) {
      this.addLocalChatHistory(newChatHistoryItem);
    }
    if (this.isIgnoreChat) {
      this.isIgnoreChat = false;
      this.updateChatUrl(this.chatId);
    }

    this.cdr.markForCheck();
  }

  private clearChatTextArea() {
    this.chatTextareaRef()?.clearTextarea();
    this.chatTextareaRef()?.isValidTextareaInput.set(true);

    this.chatFileService.resetFiles(this.chatId, [], this.queryParams);
  }

  private initParams() {
    this.destroySearchSession();
    this.unsubscribeInit();
    this.resetQuestionData();
    this.currentMessageState = this.currentQuestionId = null;
    this.pageToken = undefined;
    this.loadingNextPage = false;
    this.isIgnoreChat = false;
    this.previousScrollTop = 0;
    this.initStatus.set(null);
    this.chatId = NEW_CHAT_ID;
  }

  private resetQuestionData() {
    this.draftQuestion.set(null);
  }

  private destroySearchSession() {
    if (this.searchSession) {
      this.searchSession.destroy();
    }
    this.unsubscribeSearchSession();
  }

  deleteSession() {
    this.chatsPopupService.deleteSessionPopup(this.chatId, this.assistantData()?.id, true);
  }

  private onScroll(event: Event) {
    const target = event.target as HTMLElement;
    const scrollTop = target.scrollTop;
    const scrollHeight = target.scrollHeight;
    const clientHeight = target.clientHeight;

    const scrolledPercentage = (scrollTop / (scrollHeight - clientHeight)) * 100;

    if (scrollTop < this.previousScrollTop && scrolledPercentage <= this.MESSAGES_PER_PAGE && !this.loadingNextPage && this.pageToken) {
      this.getNextPageMessages();
    }
    this.previousScrollTop = scrollTop;
  }

  openNewAssistantPage() {
    return this.experiencesService.openAssistant(this.assistantData()?.id);
  }
}
